import { useState } from 'preact/hooks';
import type {
    CategoryBoundingBox,
    ProductImage
} from '../../../models/Product';
import './ProductImage.less';
import { getConnector } from '../../../services/Connector';
import { AspectRatio } from '../../../store/uiState';
import IconButton from '../../button/IconButton';
import { getBiggestBoundingBox } from '../../../services/features/main/ProductSearchByImageMainFeatureHandler';
import { useTranslation } from '../../../hooks/useTranslation';

export type ProductImageProps = {
    className?: string;
    isVisible: boolean;
    image: ProductImage;
    defaultWidth: number;
    aspectRatio?: AspectRatio;
    showBoundingBoxes?: boolean;
    selectedBoundingBox?: CategoryBoundingBox;
    onChangeSelectedBoundingBox?: (box?: CategoryBoundingBox) => void;
};

const ProductImage = ({
    className = '',
    isVisible,
    image,
    defaultWidth,
    aspectRatio,
    showBoundingBoxes = false,
    selectedBoundingBox,
    onChangeSelectedBoundingBox
}: ProductImageProps) => {
    const { t } = useTranslation();
    const [wasSelected, setWasSelected] = useState(isVisible);
    const [wasLoaded, setWasLoaded] = useState(false);
    const boundingBoxes = !showBoundingBoxes
        ? []
        : (image.categoryBoundingBoxes ?? []).filter(
              boundingBox =>
                  onChangeSelectedBoundingBox ||
                  boundingBox === selectedBoundingBox
          );
    const { url, preview, width = 1, height = 1 } = image;
    if (!wasSelected && isVisible) {
        setWasSelected(true);
    }
    return (
        <div
            className={`bri-ProductImage ${
                isVisible ? 'bri-visible' : ''
            } ${wasLoaded ? 'bri-loaded' : ''} ${className}`}
            style={{
                aspectRatio,
                backgroundImage: preview
                    ? `url(data:image/jpeg;base64,${preview})`
                    : `url(${getConnector().getScaledImage(url, 20)})`
            }}
        >
            {wasSelected && (
                <img
                    src={getConnector().getScaledImage(url, defaultWidth)}
                    alt={image.altText}
                    onLoad={() => setWasLoaded(true)}
                    style={{
                        aspectRatio,
                        opacity: wasLoaded ? 1 : 0
                    }}
                />
            )}
            {boundingBoxes.map(boundingBox => (
                <div
                    className={`bri-ProductImage-bounding-box${boundingBox === selectedBoundingBox ? ' bri-selected' : ''}`}
                    title={
                        boundingBox.categoryTranslatedValue ??
                        boundingBox.categoryValue
                    }
                    style={{
                        top: `${((boundingBox.top + boundingBox.height / 2) / height) * 100}%`,
                        left: `${((boundingBox.left + boundingBox.width / 2) / width) * 100}%`,
                        width: 0,
                        height: 0,
                        ...(boundingBox === selectedBoundingBox && {
                            width: `${(boundingBox.width / width) * 100}%`,
                            height: `${(boundingBox.height / height) * 100}%`
                        }),
                        opacity: selectedBoundingBox ? 1 : 0,
                        pointerEvents: selectedBoundingBox ? 'auto' : 'none'
                    }}
                    onClick={() => onChangeSelectedBoundingBox?.(boundingBox)}
                />
            ))}
            {boundingBoxes.length > 0 && (
                <>
                    <div
                        className={`bri-ProductImage-bounding-box bri-full-image ${!selectedBoundingBox ? ' bri-selected' : ''}`}
                    />
                    <IconButton
                        variant="glass"
                        iconId={selectedBoundingBox ? 'full-image' : 'clothes'}
                        onClick={() =>
                            onChangeSelectedBoundingBox?.(
                                !selectedBoundingBox
                                    ? getBiggestBoundingBox(boundingBoxes)
                                    : undefined
                            )
                        }
                        title={
                            selectedBoundingBox
                                ? t('PRODUCT_SEARCH_BY_IMAGE_USE_FULL_IMAGE')
                                : t('PRODUCT_SEARCH_BY_IMAGE_SELECT_RESULT')
                        }
                    />
                </>
            )}
        </div>
    );
};

export default ProductImage;
